import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { T, useGym } from '../../lib/gym';
import { useInterval } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Button, cx, errorText } from '../../ui/components';
import { Icon } from '../../ui/icons';

interface Msg { id?: string; role: string; content: string }

function Linkified({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const re = /(https?:\/\/[^\s)]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    parts.push(text.slice(last, m.index));
    parts.push(<a key={m.index} href={m[1]} target="_blank" rel="noreferrer noopener">{m[1]}</a>);
    last = m.index + m[1].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

export function ChatBox({ className }: { className?: string }) {
  const gym = useGym();
  const i18n = useI18n();
  const { user } = useAuth();
  const member = Boolean(user && gym.membership?.personId);
  const storageKey = `gs_chat_${gym.slug}_${member ? 'm' : 'v'}`;
  const [conversationId, setConversationId] = useState<string | null>(() => { try { return localStorage.getItem(storageKey); } catch { return null; } });
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const load = async (): Promise<void> => {
    if (!conversationId) return;
    try {
      const r = await api(`/v1/public/${gym.slug}/chat/${conversationId}`);
      setMessages(r.messages);
      setHandoff(r.conversation.status === 'handoff');
    } catch {
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
      setConversationId(null);
    }
  };
  useEffect(() => { void load(); }, []);
  useInterval(() => { void load(); }, handoff ? 8000 : null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, busy]);

  const send = async (): Promise<void> => {
    const msg = text.trim();
    if (!msg || busy) return;
    setText('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setBusy(true);
    try {
      const path = member ? `${T(gym.slug)}/chat` : `/v1/public/${gym.slug}/chat`;
      const r = await api(path, { body: { conversationId, message: msg, locale: i18n.locale, channel: 'web' } });
      if (r.conversationId !== conversationId) {
        setConversationId(r.conversationId);
        try { localStorage.setItem(storageKey, r.conversationId); } catch { /* ignore */ }
      }
      if (r.reply) setMessages((m) => [...m, { role: 'assistant', content: r.reply }]);
      setHandoff(Boolean(r.handoff));
    } catch (e) {
      setMessages((m) => [...m, { role: 'system', content: errorText(e, i18n) }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cx('chat', className)}>
      <div className="chat-log" aria-live="polite">
        <div className="chat-note">{i18n.t('chat.disclosure')}</div>
        <div className="msg msg-assistant">{i18n.t('chat.greeting', { gym: gym.gym.name })}</div>
        {messages.map((m, i) => <div key={m.id ?? i} className={`msg msg-${m.role}`}><Linkified text={m.content} /></div>)}
        {busy && <div className="msg msg-assistant typing"><span /><span /><span /></div>}
        {handoff && <div className="chat-note">{i18n.t('chat.handoff_note')}</div>}
        <div ref={endRef} />
      </div>
      <form className="chat-input" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <input className="input" value={text} maxLength={2000} placeholder={i18n.t('chat.placeholder')} onChange={(e) => setText(e.target.value)} aria-label={i18n.t('chat.placeholder')} />
        <Button type="submit" disabled={!text.trim() || busy}>{i18n.t('chat.send')}</Button>
      </form>
    </div>
  );
}

export function ChatBubble() {
  const i18n = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="chat-bubble">
      {open && (
        <div className="chat-panel">
          <div className="chat-panel-head"><strong>{i18n.t('chat.title')}</strong><button type="button" className="icon-btn" aria-label="Close" onClick={() => setOpen(false)}>×</button></div>
          <ChatBox />
        </div>
      )}
      <button type="button" className="chat-fab" aria-label={i18n.t('chat.title')} onClick={() => setOpen(!open)}><Icon name="chat" size={24} /></button>
    </div>
  );
}

export function ChatWidgetPage() {
  return <div className="embed chat-embed"><ChatBox /></div>;
}
